import { racehorse360 } from "@tsg/1st-grpc-web-rh360";
import { SortOrder } from "interfaces/SortOrder";
import { checkSortingType } from "utils/sort-utils";
import { transformViewDate } from "utils/date-utils";

type SortingFn = (
  applicant1: racehorse360.IStallApplication,
  applicant2: racehorse360.IStallApplication
) => number;

type getSorterFn = (
  order: SortOrder,
  orderBy: string,
  isReviewed: boolean
) => SortingFn;

export enum EColumns {
  TRAINER_NAME = "trainerName",
  STALLS = "stalls",
  ARRIVAL_DATE = "arrivalDate",
  ROOMS = "rooms",
  FACILITY_PREFERENCE_1 = "choice1",
  FACILITY_PREFERENCE_2 = "choice2",
  APPROVED = "approved",
  DENIED = "denied",
  AWAITING = "awaiting"
}

export enum EApplicantsTableTab {
  APPLICANTS = 0,
  REVIEWED = 1,
  IN_PROGRESS = 2
}

export interface IApplicantsTabsStatuses {
  isApplicantsTab: boolean;
  isReviewedTab: boolean;
  isInProgressTab: boolean;
}

export interface IColumn {
  id: EColumns;
  label: string;
  value: EColumns;
  isNonSortable?: boolean;
  render?: (
    application: racehorse360.IStallApplication,
    isReviewedTab: boolean
  ) => React.ReactNode;
}

const INSERTING_ELEMENT_POSITION = 2;

const valueRenderers = {
  [EColumns.TRAINER_NAME]: (application: racehorse360.IStallApplication) =>
    application.contactFullName,

  [EColumns.STALLS]: (
    application: racehorse360.IStallApplication,
    isReviewedTab: boolean
  ) =>
    isReviewedTab
      ? application.stallsNumberFirstChoice +
        application.stallsNumberSecondChoice
      : application.stableEntriesCount,

  [EColumns.ROOMS]: (
    application: racehorse360.IStallApplication,
    isReviewedTab: boolean
  ) =>
    isReviewedTab
      ? application.sleepingRoomsNumberFirstChoice +
        application.sleepingRoomsNumberSecondChoice
      : application.sleepingRoomsNumber,

  [EColumns.ARRIVAL_DATE]: (application: racehorse360.IStallApplication) =>
    transformViewDate(application.arrivalDate, null, "MM/dd/yy"),

  [EColumns.FACILITY_PREFERENCE_1]: (
    application: racehorse360.IStallApplication
  ) => application.firstChoiceFacility.code,

  [EColumns.FACILITY_PREFERENCE_2]: (
    application: racehorse360.IStallApplication
  ) => application.secondChoiceFacility.code,

  [EColumns.APPROVED]: (application: racehorse360.IStallApplication) =>
    application.stableEntriesApprovedCount,

  [EColumns.DENIED]: (application: racehorse360.IStallApplication) =>
    application.stableEntriesDeniedCount,

  [EColumns.AWAITING]: (application: racehorse360.IStallApplication) =>
    application.stableEntriesAwaitingCount
};

const addElementsToArray = (
  array: IColumn[],
  newElements: IColumn[],
  startPosition: number
): IColumn[] => {
  const result = [...array];

  newElements.forEach((element, index) => {
    result.splice(startPosition, 0, newElements[index]);
    startPosition++;
  });

  return result;
};

const applicantsTabColumns: IColumn[] = [
  {
    id: EColumns.TRAINER_NAME,
    label: "Trainer Name",
    value: EColumns.TRAINER_NAME,
    render: valueRenderers[EColumns.TRAINER_NAME]
  },
  {
    id: EColumns.STALLS,
    label: "Stalls",
    value: EColumns.STALLS,
    render: valueRenderers[EColumns.STALLS]
  },
  {
    id: EColumns.ARRIVAL_DATE,
    label: "Arrival Date",
    value: EColumns.ARRIVAL_DATE,
    render: valueRenderers[EColumns.ARRIVAL_DATE]
  },
  {
    id: EColumns.ROOMS,
    label: "Rooms",
    value: EColumns.ROOMS,
    render: valueRenderers[EColumns.ROOMS]
  },
  {
    id: EColumns.FACILITY_PREFERENCE_1,
    label: "1st Choice",
    value: EColumns.FACILITY_PREFERENCE_1,
    render: valueRenderers[EColumns.FACILITY_PREFERENCE_1]
  },
  {
    id: EColumns.FACILITY_PREFERENCE_2,
    label: "2nd Choice",
    value: EColumns.FACILITY_PREFERENCE_2,
    render: valueRenderers[EColumns.FACILITY_PREFERENCE_2]
  }
];

const reviewedTabColumns = addElementsToArray(
  applicantsTabColumns,
  [
    {
      id: EColumns.APPROVED,
      label: "Approved",
      value: EColumns.APPROVED,
      render: valueRenderers[EColumns.APPROVED]
    },
    {
      id: EColumns.DENIED,
      label: "Denied",
      value: EColumns.DENIED,
      render: valueRenderers[EColumns.DENIED]
    }
  ],
  INSERTING_ELEMENT_POSITION
);

const inProgressTabColumns = addElementsToArray(
  reviewedTabColumns,
  [
    {
      id: EColumns.AWAITING,
      label: "Awaiting",
      value: EColumns.AWAITING,
      render: valueRenderers[EColumns.AWAITING]
    }
  ],
  INSERTING_ELEMENT_POSITION
);

export const tableColumnsSets = [
  applicantsTabColumns,
  reviewedTabColumns,
  inProgressTabColumns
];

const getSleepingRoomsSortValue = (
  applicant: racehorse360.IStallApplication,
  isReviewedTab: boolean
): number => {
  return isReviewedTab
    ? Number(applicant.sleepingRoomsNumberFirstChoice) +
        Number(applicant.sleepingRoomsNumberSecondChoice)
    : applicant.sleepingRoomsNumber;
};

const getStallsSortValue = (
  applicant: racehorse360.IStallApplication,
  isReviewedTab: boolean
): number => {
  return isReviewedTab
    ? Number(applicant.stallsNumberFirstChoice) +
        Number(applicant.stallsNumberSecondChoice)
    : applicant.stableEntriesCount;
};

export const getStallApplicantsSortFunction: getSorterFn = (
  order: SortOrder,
  orderBy: string,
  isReviewedTab: boolean
) => {
  const { isAsc } = checkSortingType(order);
  const multiplier = isAsc ? 1 : -1;

  switch (orderBy) {
    case EColumns.TRAINER_NAME:
      return (
        applicant1: racehorse360.StallApplication,
        applicant2: racehorse360.StallApplication
      ) =>
        applicant1.contactFullName.localeCompare(applicant2.contactFullName) *
        multiplier;
    case EColumns.STALLS:
      return (
        applicant1: racehorse360.IStallApplication,
        applicant2: racehorse360.IStallApplication
      ) => {
        const value1 = getStallsSortValue(applicant1, isReviewedTab);
        const value2 = getStallsSortValue(applicant2, isReviewedTab);

        return (value1 - value2) * multiplier;
      };
    case EColumns.ARRIVAL_DATE:
      return (
        applicant1: racehorse360.StallApplication,
        applicant2: racehorse360.StallApplication
      ) =>
        applicant1.arrivalDate.localeCompare(applicant2.arrivalDate) *
        multiplier;
    case EColumns.ROOMS:
      return (
        applicant1: racehorse360.IStallApplication,
        applicant2: racehorse360.IStallApplication
      ) => {
        const value1 = getSleepingRoomsSortValue(applicant1, isReviewedTab);
        const value2 = getSleepingRoomsSortValue(applicant2, isReviewedTab);

        return (value1 - value2) * multiplier;
      };
    case EColumns.FACILITY_PREFERENCE_1:
      return (
        applicant1: racehorse360.StallApplication,
        applicant2: racehorse360.StallApplication
      ) =>
        applicant1.firstChoiceFacility.code.localeCompare(
          applicant2.firstChoiceFacility.code
        ) * multiplier;
    case EColumns.FACILITY_PREFERENCE_2:
      return (
        applicant1: racehorse360.StallApplication,
        applicant2: racehorse360.StallApplication
      ) =>
        applicant1.secondChoiceFacility.code.localeCompare(
          applicant2.secondChoiceFacility.code
        ) * multiplier;
    case EColumns.APPROVED:
      return (
        applicant1: racehorse360.StallApplication,
        applicant2: racehorse360.StallApplication
      ) =>
        (applicant1.stableEntriesApprovedCount -
          applicant2.stableEntriesApprovedCount) *
        multiplier;
    case EColumns.DENIED:
      return (
        applicant1: racehorse360.StallApplication,
        applicant2: racehorse360.StallApplication
      ) =>
        (applicant1.stableEntriesDeniedCount -
          applicant2.stableEntriesDeniedCount) *
        multiplier;
    case EColumns.AWAITING:
      return (
        applicant1: racehorse360.StallApplication,
        applicant2: racehorse360.StallApplication
      ) =>
        (applicant1.stableEntriesAwaitingCount -
          applicant2.stableEntriesAwaitingCount) *
        multiplier;
    default:
      console.error("there is no such sorting key");
      return (
        applicant1: racehorse360.StallApplication,
        applicant2: racehorse360.StallApplication
      ) =>
        applicant1.trainer.lastName.localeCompare(applicant2.trainer.lastName) *
        multiplier;
  }
};

export const getStallApplicationStatusByTab = (
  tabValue: EApplicantsTableTab
): racehorse360.StallApplicationStatus => {
  switch (tabValue) {
    case EApplicantsTableTab.REVIEWED: {
      return racehorse360.StallApplicationStatus
        .STALL_APPLICATION_STATUS_COMPLETE;
    }
    case EApplicantsTableTab.IN_PROGRESS: {
      return racehorse360.StallApplicationStatus
        .STALL_APPLICATION_STATUS_UNDER_REVIEW;
    }
    default: {
      return racehorse360.StallApplicationStatus
        .STALL_APPLICATION_STATUS_APPLIED;
    }
  }
};

export const getTabByStallApplicationStatus = (
  status: racehorse360.StallApplicationStatus
): EApplicantsTableTab => {
  switch (status) {
    case racehorse360.StallApplicationStatus
      .STALL_APPLICATION_STATUS_COMPLETE: {
      return EApplicantsTableTab.REVIEWED;
    }
    case racehorse360.StallApplicationStatus
      .STALL_APPLICATION_STATUS_UNDER_REVIEW: {
      return EApplicantsTableTab.IN_PROGRESS;
    }
    default: {
      return EApplicantsTableTab.APPLICANTS;
    }
  }
};

export const checkCurrentApplicantsTab = (
  tab: EApplicantsTableTab
): IApplicantsTabsStatuses => ({
  isApplicantsTab: tab === EApplicantsTableTab.APPLICANTS,
  isReviewedTab: tab === EApplicantsTableTab.REVIEWED,
  isInProgressTab: tab === EApplicantsTableTab.IN_PROGRESS
});
